import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import SEO from "./components/SEO";
import { useSyncLanguageWithUrl } from "./lib/i18n-routing";
import { trackPageview } from "./lib/analytics";
import { captureAttribution } from "./lib/utm";
// Index page is eager-loaded — it is the LCP route and must render immediately.
import Index from "./pages/Index";
// WhatsAppButton is a fixed bottom-right CTA — not critical for first paint.
// Lazy-loading keeps it out of the initial bundle to improve Speed Index.
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
// All other routes are lazy-loaded to drop ~150 KB from the initial bundle.
// Suspense fallback is intentionally null — pages render quickly and a flash
// of a spinner would be more disruptive than a brief blank state.
const Pricing = lazy(() => import("./pages/Pricing"));
const WhoWeServe = lazy(() => import("./pages/WhoWeServe"));
const Restoration = lazy(() => import("./pages/Restoration"));
const GeneralContractor = lazy(() => import("./pages/GeneralContractor"));
const HVAC = lazy(() => import("./pages/HVAC"));
const Landscaping = lazy(() => import("./pages/Landscaping"));
const Roofing = lazy(() => import("./pages/Roofing"));
const Solar = lazy(() => import("./pages/Solar"));
const PhotoVideoCapture = lazy(() => import("./pages/PhotoVideoCapture"));
const ProjectTimeline = lazy(() => import("./pages/ProjectTimeline"));
const Annotations = lazy(() => import("./pages/Annotations"));
const TagsLabels = lazy(() => import("./pages/TagsLabels"));
const ProjectFeed = lazy(() => import("./pages/ProjectFeed"));
const AIInspection = lazy(() => import("./pages/AIInspection"));
const ComplianceManagement = lazy(() => import("./pages/ComplianceManagement"));
const DocumentIntelligence = lazy(() => import("./pages/DocumentIntelligence"));
const ProjectDashboard = lazy(() => import("./pages/ProjectDashboard"));
const RiskManagement = lazy(() => import("./pages/RiskManagement"));
const Integrations = lazy(() => import("./pages/Integrations"));
const TrackProgress = lazy(() => import("./pages/TrackProgress"));
const Invoicing = lazy(() => import("./pages/Invoicing"));
const ShareUpdates = lazy(() => import("./pages/ShareUpdates"));
const TeamWorkflows = lazy(() => import("./pages/TeamWorkflows"));
const Reports = lazy(() => import("./pages/Reports"));
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const AIEstimating = lazy(() => import("./pages/AIEstimating"));
const ProcurementAI = lazy(() => import("./pages/ProcurementAI"));
const MarketingAI = lazy(() => import("./pages/MarketingAI"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Contact = lazy(() => import("./pages/Contact"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Webinar = lazy(() => import("./pages/Webinar"));
const About = lazy(() => import("./pages/About"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
// Trade-specific pages
const TradeGeneralContracting = lazy(() => import("./pages/trades/GeneralContracting"));
const TradeVillaConstruction = lazy(() => import("./pages/trades/VillaConstruction"));
const TradeRenovation = lazy(() => import("./pages/trades/Renovation"));
const TradeDemolition = lazy(() => import("./pages/trades/Demolition"));
const TradeLandscaping = lazy(() => import("./pages/trades/LandscapingTrade"));
const TradeMEP = lazy(() => import("./pages/trades/MEP"));
const TradeSolar = lazy(() => import("./pages/trades/SolarTrade"));
const TradeDataCenters = lazy(() => import("./pages/trades/DataCenters"));
const TradeCivilEngineering = lazy(() => import("./pages/trades/CivilEngineering"));
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));

const RouteFallback = () => (
  <div className="min-h-screen bg-[#f3efe8] flex items-center justify-center">
    <div className="w-10 h-10 rounded-full border-2 border-black/15 border-t-black/60 animate-spin" />
  </div>
);

const LazyToaster = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const show = () => setReady(true);
    if (document.readyState === "complete") {
      window.setTimeout(show, 1000);
      return;
    }
    window.addEventListener("load", show, { once: true });
    return () => window.removeEventListener("load", show);
  }, []);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
    </Suspense>
  );
};

const AppRoutes = () => {
  useSyncLanguageWithUrl();
  const location = useLocation();
  useEffect(() => {
    captureAttribution();
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return (
    <>
      <ScrollToTop />
      <SEO />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {["/", "/ar", "/hi", "/ru"].map((prefix) => (
            <Route key={prefix} path={prefix}>
              <Route index element={<Index />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="who-we-serve" element={<WhoWeServe />} />
              <Route path="restoration" element={<Restoration />} />
              <Route path="general-contractor" element={<GeneralContractor />} />
              <Route path="hvac" element={<HVAC />} />
              <Route path="landscaping" element={<Landscaping />} />
              <Route path="roofing" element={<Roofing />} />
              <Route path="solar" element={<Solar />} />
              <Route path="construction-photo-documentation" element={<PhotoVideoCapture />} />
              <Route path="construction-scheduling" element={<ProjectTimeline />} />
              <Route path="annotations" element={<Annotations />} />
              <Route path="tags-labels" element={<TagsLabels />} />
              <Route path="construction-daily-log" element={<ProjectFeed />} />
              <Route path="punch-list-software" element={<AIInspection />} />
              <Route path="construction-compliance" element={<ComplianceManagement />} />
              <Route path="construction-document-management" element={<DocumentIntelligence />} />
              <Route path="project-dashboard" element={<ProjectDashboard />} />
              <Route path="construction-risk-management" element={<RiskManagement />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="construction-progress-tracking" element={<TrackProgress />} />
              <Route path="contractor-invoicing" element={<Invoicing />} />
              <Route path="share-updates" element={<ShareUpdates />} />
              <Route path="team-workflows" element={<TeamWorkflows />} />
              <Route path="construction-reports" element={<Reports />} />
              <Route path="ai-for-construction" element={<AIAssistant />} />
              <Route path="ai-construction-estimating" element={<AIEstimating />} />
              <Route path="procurement-ai" element={<ProcurementAI />} />
              <Route path="marketing-ai" element={<MarketingAI />} />
              <Route path="portfolio" element={<Portfolio />} />
              <Route path="contact" element={<Contact />} />
              <Route path="terms-of-service" element={<TermsOfService />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="cookie-policy" element={<CookiePolicy />} />
              <Route path="blog" element={<Blog />} />
              <Route path="blog/:slug" element={<BlogPost />} />
              <Route path="webinar" element={<Webinar />} />
              <Route path="about" element={<About />} />
              <Route path="affiliate" element={<Affiliate />} />
              {/* Trade-specific landing pages */}
              <Route path="ai-for-general-contractors" element={<TradeGeneralContracting />} />
              <Route path="ai-for-villa-construction" element={<TradeVillaConstruction />} />
              <Route path="ai-for-renovation" element={<TradeRenovation />} />
              <Route path="ai-for-demolition" element={<TradeDemolition />} />
              <Route path="ai-for-landscaping" element={<TradeLandscaping />} />
              <Route path="ai-for-mep" element={<TradeMEP />} />
              <Route path="ai-for-solar" element={<TradeSolar />} />
              <Route path="ai-for-data-centers" element={<TradeDataCenters />} />
              <Route path="ai-for-civil-engineering" element={<TradeCivilEngineering />} />
              {/* Feature-prefixed blog posts: /:featureSlug/:articleSlug */}
              <Route path=":featureSlug/:articleSlug" element={<BlogPost />} />
            </Route>
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Suspense fallback={null}>
        <WhatsAppButton />
      </Suspense>
    </>
  );
};

const App = () => (
  <BrowserRouter>
    <LazyToaster />
    <AppRoutes />
  </BrowserRouter>
);

// Named exports for SSR — entry-server.tsx wraps AppRoutes in StaticRouter
// instead of BrowserRouter. LazyToaster is client-only (uses window/document).
export { AppRoutes, LazyToaster };
export default App;
